import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar, CartesianGrid } from 'recharts';
import api from '../admin/api';

const KPI = ({ label, value, testId }) => (
  <div data-testid={testId} className="border border-line bg-paper p-5">
    <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-muted2">{label}</p>
    <p className="mt-2 font-serif text-4xl font-medium text-ink">{value ?? '—'}</p>
  </div>
);

const Panel = ({ title, action, children, testId }) => (
  <div data-testid={testId} className="border border-line bg-paper p-6">
    <div className="mb-5 flex items-center justify-between">
      <h2 className="font-serif text-xl font-medium text-ink">{title}</h2>
      {action}
    </div>
    {children}
  </div>
);

const timeAgo = (ts) => {
  const s = Math.max(0, (Date.now() - new Date(ts)) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

const DashboardPage = () => {
  const [overview, setOverview] = useState(null);
  const [series, setSeries] = useState([]);
  const [topPages, setTopPages] = useState([]);
  const [sources, setSources] = useState([]);
  const [devices, setDevices] = useState([]);
  const [types, setTypes] = useState([]);
  const [leads, setLeads] = useState([]);
  const [chats, setChats] = useState([]);
  const [days, setDays] = useState(28);

  useEffect(() => {
    const loadOverview = async () => {
      try {
        const [o, l, c] = await Promise.all([api.get('/admin/overview'), api.get('/admin/leads'), api.get('/admin/chats')]);
        setOverview(o.data);
        setLeads(l.data.slice(0, 5));
        setChats(c.data.filter((x) => x.status === 'active').slice(0, 5));
      } catch (e) { /* silent */ }
    };
    loadOverview();
    const t = setInterval(loadOverview, 10000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    Promise.all([
      api.get(`/admin/analytics/timeseries?days=${days}`),
      api.get(`/admin/analytics/top-pages?days=${days}`),
      api.get(`/admin/analytics/sources?days=${days}`),
      api.get(`/admin/analytics/devices?days=${days}`),
      api.get(`/admin/analytics/visitor-types?days=${days}`),
    ]).then(([s, tp, so, d, vt]) => {
      setSeries(s.data);
      setTopPages(tp.data);
      setSources(so.data);
      setDevices(d.data);
      setTypes(vt.data);
    }).catch(() => {});
  }, [days]);

  return (
    <div data-testid="admin-dashboard" className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="font-serif text-3xl font-medium text-ink">Dashboard</h1>
        <div className="flex gap-2">
          {[7, 28].map((d) => (
            <button key={d} data-testid={`range-${d}d`} onClick={() => setDays(d)}
              className={`border px-4 py-2 text-xs font-semibold uppercase tracking-[0.15em] transition-colors ${days === d ? 'border-bronze bg-bronze text-paper' : 'border-line text-muted2 hover:text-ink'}`}>
              {d} Days
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 md:grid-cols-4 xl:grid-cols-7">
        <KPI label="Online Now" value={overview?.online_now} testId="kpi-online" />
        <KPI label="Last 30 Min" value={overview?.last_30min} testId="kpi-30min" />
        <KPI label="Today (24h)" value={overview?.last_24h} testId="kpi-today" />
        <KPI label="7 Days" value={overview?.last_7d} testId="kpi-7d" />
        <KPI label="28 Days" value={overview?.last_28d} testId="kpi-28d" />
        <KPI label="New Leads" value={overview?.new_leads} testId="kpi-leads" />
        <KPI label="Active Chats" value={overview?.active_chats} testId="kpi-chats" />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Panel title="Visitor Activity" testId="panel-visitor-chart">
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={series}>
              <CartesianGrid stroke="#E5E2DA" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#595959' }} tickFormatter={(d) => d.slice(5)} />
              <YAxis tick={{ fontSize: 11, fill: '#595959' }} allowDecimals={false} width={28} />
              <Tooltip contentStyle={{ background: '#161615', border: 'none', color: '#F4F3EF', fontSize: 12 }} />
              <Area type="monotone" dataKey="pageviews" stroke="#8FAF97" fill="#8FAF97" fillOpacity={0.15} name="Page views" />
              <Area type="monotone" dataKey="visitors" stroke="#0B3D0B" fill="#0B3D0B" fillOpacity={0.18} name="Visitors" />
            </AreaChart>
          </ResponsiveContainer>
        </Panel>
        <Panel title="Lead Activity" testId="panel-lead-chart">
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={series}>
              <CartesianGrid stroke="#E5E2DA" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#595959' }} tickFormatter={(d) => d.slice(5)} />
              <YAxis tick={{ fontSize: 11, fill: '#595959' }} allowDecimals={false} width={28} />
              <Tooltip contentStyle={{ background: '#161615', border: 'none', color: '#F4F3EF', fontSize: 12 }} />
              <Bar dataKey="leads" fill="#0B3D0B" name="Leads" />
            </BarChart>
          </ResponsiveContainer>
        </Panel>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <Panel title="Top Pages" testId="panel-top-pages">
          {topPages.length === 0 && <p className="text-sm text-muted2">No page views recorded yet.</p>}
          <ul className="space-y-3">
            {topPages.map((p) => (
              <li key={p.path} className="flex items-center justify-between gap-4 text-sm">
                <span className="truncate text-ink/80">{p.path}</span>
                <span className="font-semibold text-ink">{p.views}</span>
              </li>
            ))}
          </ul>
        </Panel>
        <Panel title="Traffic Sources" testId="panel-sources">
          {sources.length === 0 && <p className="text-sm text-muted2">No traffic recorded yet.</p>}
          <ul className="space-y-3">
            {sources.map((s) => (
              <li key={s.source} className="flex items-center justify-between gap-4 text-sm">
                <span className="truncate text-ink/80">{s.source || 'Direct'}</span>
                <span className="font-semibold text-ink">{s.visitors}</span>
              </li>
            ))}
          </ul>
          <div className="mt-6 border-t border-line pt-4">
            <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-muted2">Devices</p>
            <div className="flex flex-wrap gap-2">
              {devices.map((d) => (
                <span key={d.device} className="border border-line px-3 py-1.5 text-xs text-ink/80">{d.device} · {d.visitors}</span>
              ))}
            </div>
            <p className="mb-2 mt-5 text-[10px] font-semibold uppercase tracking-[0.2em] text-muted2">New vs Returning</p>
            <div className="flex flex-wrap gap-2">
              {types.map((t) => (
                <span key={t.type} className="border border-line px-3 py-1.5 text-xs text-ink/80">{t.type} · {t.count}</span>
              ))}
            </div>
          </div>
        </Panel>
        <Panel title="Recent Leads" testId="panel-recent-leads" action={<Link to="/admin/leads" className="text-xs font-semibold uppercase tracking-[0.15em] text-bronze">View All</Link>}>
          {leads.length === 0 && <p className="text-sm text-muted2">No leads yet.</p>}
          <ul className="space-y-3">
            {leads.map((l) => (
              <li key={l.id} className="flex items-center justify-between gap-4 text-sm">
                <div className="min-w-0">
                  <p className="truncate font-medium text-ink">{l.name}</p>
                  <p className="truncate text-xs text-muted2">{l.project_type} · {l.city || '—'}</p>
                </div>
                <span className={`shrink-0 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider ${l.status === 'New' ? 'bg-bronze/10 text-bronze' : 'bg-paper2 text-muted2'}`}>{l.status}</span>
              </li>
            ))}
          </ul>
        </Panel>
      </div>

      <Panel title="Active Chat Conversations" testId="panel-active-chats" action={<Link to="/admin/chat" className="text-xs font-semibold uppercase tracking-[0.15em] text-bronze">Open Inbox</Link>}>
        {chats.length === 0 && <p className="text-sm text-muted2">No active conversations.</p>}
        <ul className="divide-y divide-line">
          {chats.map((c) => (
            <li key={c.id}>
              <Link to={`/admin/chat/${c.id}`} className="flex items-center justify-between py-3 text-sm">
                <div>
                  <span className="font-medium text-ink">{c.visitor_name}</span>
                  <span className="ml-3 text-xs text-muted2">{c.last_message || 'No messages yet'}</span>
                </div>
                <span className="text-xs text-muted2">{timeAgo(c.last_activity)}</span>
              </Link>
            </li>
          ))}
        </ul>
      </Panel>
    </div>
  );
};

export default DashboardPage;
